import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';

// MUI components
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemAvatar';
import PermIdentityTwoToneIcon from '@mui/icons-material/PermIdentityTwoTone';
import AccountBalanceTwoToneIcon from '@mui/icons-material/AccountBalanceTwoTone';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import EventAvailableTwoToneIcon from '@mui/icons-material/EventAvailableTwoTone';
import Card from '@mui/material/Card';
import {Grid} from '@mui/material';
import {useMediaQuery, useTheme} from '@mui/material';

// Custom components
import AccordionTable from 'components/accordion-table';
import PatientResources from './PatientResources';
import ManagingOrganizations from './ManagingOrganizations';
import Restrictions from './Restrictions';

// Styled
import {StyledDataWrapper} from 'components/styled';

// Constants and utils
import {
  constructPatientIdentifiers,
  constructIdentifiers,
  formatOrganizationTypes,
  formatDateTimeWithTimezone
} from './utils';
import {ORGANIZATION, PATIENT, ORGANIZATION_RESTRICTION, PATIENT_RESTRICTION} from 'constants.js';
import {
  SEARCH_RESULTS_COMPONENT_TEST_ID,
  SEARCH_RESULTS_PATIENT_ICON_TEST_ID,
  SEARCH_RESULTS_ORGANIZATION_ICON_TEST_ID,
  SEARCH_RESULTS_RESOURCE_TEST_ID,
  SEARCH_RESULTS_ORGANIZATION_LAST_FULL_UPLOAD_DATE_TEXT_TEST_ID
} from 'testidConstants.js';
import {memo} from 'react';

const SearchResults = ({resultData, searchPerformedOn}) => {
  const [formattedIdentifiers, setFormattedIdentifiers] = useState([]);
  const [resourceRestrictionByPatient, setResourceRestrictionByPatient] = useState([]);
  const mobile = useMediaQuery(useTheme().breakpoints.down('md'));

  useEffect(() => {
    if (resultData.identifiers)
      setFormattedIdentifiers(
        searchPerformedOn === PATIENT
          ? constructPatientIdentifiers(resultData.identifiers, resultData.hasBsnIdentifier)
          : constructIdentifiers(resultData.identifiers)
      );
  }, [resultData]);

  useEffect(() => {
    if (searchPerformedOn === PATIENT) {
      if (resultData.isAccessRestricted) {
        setResourceRestrictionByPatient(
          resultData.dataAccessRestrictions
            .filter((restriction) => restriction.isActive)
            .map((restriction) => {
              if (restriction.code === ORGANIZATION_RESTRICTION) return ORGANIZATION_RESTRICTION;
              else return PATIENT_RESTRICTION;
            })
            .filter((index, i, code) => code.indexOf(index) == i)
        );
      }
    }
  }, [resultData]);

  return (
    <StyledDataWrapper data-testid={SEARCH_RESULTS_COMPONENT_TEST_ID}>
      <Grid container direction="row">
        <Grid item xs={12} md={3}>
          <Card style={{padding: '0 16px', marginTop: 16}} variant="outlined">
            <List>
              {searchPerformedOn === ORGANIZATION && (
                <ListItem>
                  <ListItemText primary={resultData.name} />
                </ListItem>
              )}
              <ListItem>
                <ListItemIcon>
                  {searchPerformedOn === PATIENT ? (
                    <PermIdentityTwoToneIcon data-testid={SEARCH_RESULTS_PATIENT_ICON_TEST_ID} color="primary" />
                  ) : (
                    <AccountBalanceTwoToneIcon data-testid={SEARCH_RESULTS_ORGANIZATION_ICON_TEST_ID} color="primary" />
                  )}
                </ListItemIcon>
                <ListItemText
                  style={{overflowWrap: 'anywhere'}}
                  data-testid={SEARCH_RESULTS_RESOURCE_TEST_ID}
                  primary={resultData.id}
                  secondary="FHIR resource ID"
                />
              </ListItem>
              {searchPerformedOn === ORGANIZATION && (
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <InfoTwoToneIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={formatOrganizationTypes(resultData.types)} secondary="Organization type" />
                  </ListItem>
                  {resultData?.lastFullUploadDate && (
                    <ListItem style={{paddingLeft: 0}}>
                      <ListItemIcon>
                        <EventAvailableTwoToneIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={formatDateTimeWithTimezone(resultData.lastFullUploadDate)}
                        secondary="Last full upload date"
                        data-testid={SEARCH_RESULTS_ORGANIZATION_LAST_FULL_UPLOAD_DATE_TEXT_TEST_ID}
                      />
                    </ListItem>
                  )}
                </List>
              )}
            </List>
            <Restrictions resultData={resultData} searchPerformedOn={searchPerformedOn} />
          </Card>
        </Grid>
        <Grid
          item
          container
          xs={12}
          md={9}
          style={{paddingLeft: mobile ? 0 : 16, marginTop: mobile ? 0 : 16, display: mobile && 'block'}}
          direction="column"
        >
          <AccordionTable
            accordionHeader={`${searchPerformedOn} Identifiers`}
            tableData={formattedIdentifiers}
            fieldNames={[
              {key: 'system', value: 'System'},
              {key: 'value', value: 'Value'}
            ]}
            expanded={true}
            resourceId={resultData.id}
          ></AccordionTable>
          {searchPerformedOn === PATIENT && (
            <>
              <ManagingOrganizations managingOrganizations={resultData.managingOrganizations}></ManagingOrganizations>
              <PatientResources
                resources={resultData.resources}
                patientId={resultData.id}
                patientRestriction={{
                  isRestricted: resultData.isAccessRestricted,
                  restrictionCode: resourceRestrictionByPatient
                }}
              ></PatientResources>
            </>
          )}
        </Grid>
      </Grid>
    </StyledDataWrapper>
  );
};

SearchResults.propTypes = {
  resultData: PropTypes.object,
  foundItemsNumber: PropTypes.number,
  searchPerformedOn: PropTypes.string,
  showResultCount: PropTypes.bool
};

export default memo(SearchResults);
